import { useEffect, useState } from 'react';
import { getQmFeatureFlagsRequest, QmFeatureFlagsResult, subscribeQmFeatureFlagUpdates } from '../api/qmCrud';
import { captureError } from '../common/sentry';

const fallbackFlags: QmFeatureFlagsResult = { qmNewDashboardEnabled: false };
const STORAGE_KEY = 'ftl.qmFeatureFlags.v1';
const MEMORY_CACHE_TTL_MS = 5 * 60 * 1000;

let cachedFlags: QmFeatureFlagsResult | null = null;
let cachedFlagsExpiresAt = 0;
let inflightRequest: Promise<QmFeatureFlagsResult> | null = null;

function readStoredFlags(): QmFeatureFlagsResult | null {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as QmFeatureFlagsResult;
    if (typeof parsed?.qmNewDashboardEnabled !== 'boolean') {
      return null;
    }

    return parsed;
  } catch {
    return null;
  }
}

function persistFlags(flags: QmFeatureFlagsResult) {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(flags));
  } catch {
    // Ignore storage errors; runtime value remains authoritative.
  }
}

async function fetchQmFeatureFlags(): Promise<QmFeatureFlagsResult> {
  if (cachedFlags && cachedFlagsExpiresAt > Date.now()) {
    return cachedFlags;
  }

  if (!inflightRequest) {
    inflightRequest = getQmFeatureFlagsRequest()
      .then((flags) => {
        cachedFlags = flags;
        cachedFlagsExpiresAt = Date.now() + MEMORY_CACHE_TTL_MS;
        persistFlags(flags);
        return flags;
      })
      .finally(() => {
        inflightRequest = null;
      });
  }

  return inflightRequest;
}

export function useQmFeatureFlags() {
  const [storedFlags] = useState<QmFeatureFlagsResult | null>(() => readStoredFlags());
  const [flags, setFlags] = useState<QmFeatureFlagsResult>(cachedFlags ?? storedFlags ?? fallbackFlags);
  const [isLoading, setIsLoading] = useState<boolean>(!(cachedFlags || storedFlags));

  useEffect(() => {
    let isMounted = true;

    const refreshFlags = async () => {
      try {
        const resolvedFlags = await fetchQmFeatureFlags();
        if (!isMounted) return;
        setFlags(resolvedFlags);
        setIsLoading(false);
      } catch (error) {
        captureError(error, 'useQmFeatureFlags-refreshFlags');
        if (!isMounted) return;

        if (storedFlags) {
          setFlags(storedFlags);
        } else {
          // Fail closed to OFF and stop loading so the legacy QM button can render.
          setFlags(fallbackFlags);
        }

        setIsLoading(false);
      }
    };

    refreshFlags();

    const unsubscribe = subscribeQmFeatureFlagUpdates((nextFlags) => {
      cachedFlags = nextFlags;
      cachedFlagsExpiresAt = Date.now() + MEMORY_CACHE_TTL_MS;
      persistFlags(nextFlags);

      if (!isMounted) return;

      setFlags(nextFlags);
      setIsLoading(false);
    });

    return () => {
      isMounted = false;
      unsubscribe();
    };
  }, []);

  return {
    ...flags,
    isLoading
  };
}
